<template>
  <a-form layout="vertical" class="flex-grow-1 d-flex flex-column overflow-y-hidden" @submit="onSubmit">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">Add Users</span>
    </div>
    <div class="a-body p-20-px flex-grow-1 d-flex flex-column overflow-y-auto slide-up-enter-active">
      <div v-if="createdUsers.length" class="success-container mb-3">
        <div>
          <a-icon type="check-circle" theme="filled" class="success-icon" />
        </div>
        <div class="ml-3">
          <p class="mb-1 notice-text">
            <strong>{{ createdUsers.length }} user(s) created.</strong>
          </p>
          <p v-if="rbacCopied.count" class="mb-1 notice-text">
            RBAC copied from {{ rbacCopied.from }} to {{ rbacCopied.count }} user(s).
          </p>
          <p class="mb-2 notice-text">
            Passwords are only available in the CSV. Download it before leaving this page.
          </p>
          <a-button size="large" @click="downloadCsv">
            <a-icon type="download" /> Download CSV
          </a-button>
        </div>
      </div>
      <div v-if="failures.length" class="error-container mb-3">
        <div>
          <a-icon type="close-circle" theme="filled" class="error-icon" />
        </div>
        <div class="ml-3">
          <p v-for="failure in failures" :key="failure" class="mb-1 notice-text">
            {{ failure }}
          </p>
        </div>
      </div>

      <div class="defaults-bar mb-3">
        <div class="defaults-title mb-2">
          Defaults for new rows
        </div>
        <div class="defaults-fields">
          <div class="defaults-field col-expiry">
            <label class="defaults-label">
              Expiry date
            </label>
            <a-date-picker
              v-model="defaults.expiryDate"
              class="full-w"
              placeholder="Choose Date"
              :disabled="isSaving"
              :disabled-date="disabledDate"
              :disabled-time="disabledTime"
              :show-time="{ format: 'HH:mm' }"
              :format="DATE_FORMAT.DDMMMYYYYHHmm"
            />
          </div>
          <div v-for="plan in PLANS" :key="plan.field" class="defaults-field col-limit">
            <label class="defaults-label">
              {{ plan.label }}
            </label>
            <a-input v-model="defaults[plan.field]" :disabled="isSaving" />
          </div>
          <div class="defaults-field col-rbac">
            <label class="defaults-label">
              Copy RBAC from
            </label>
            <a-select
              v-model="rbacSourceId"
              show-search
              allow-clear
              placeholder="None"
              option-filter-prop="children"
              :loading="isLoadingUsers"
              :disabled="isSaving"
            >
              <a-select-option v-for="user in users" :key="user.customerId" :value="user.customerId">
                {{ user.userName }} (#{{ user.customerId }})
              </a-select-option>
            </a-select>
            <span v-if="rbacSourceUser" class="defaults-hint">
              {{ rbacHint }}
            </span>
          </div>
        </div>
      </div>

      <div class="d-flex align-items-center justify-content-between mb-2">
        <div class="d-flex align-items-center">
          <label class="users-label">
            Users
          </label>
          <span class="users-count ml-2">
            ({{ bulkMode ? bulkParsed.valid.length : rows.length }})
          </span>
        </div>
        <a-button type="link" size="small" :disabled="isSaving" @click="toggleBulkMode">
          <a-icon :type="bulkMode ? 'unordered-list' : 'edit'" />
          {{ bulkMode ? 'Switch to single input' : 'Bulk add' }}
        </a-button>
      </div>

      <template v-if="!bulkMode">
        <div class="user-table-wrapper">
          <table class="user-table">
            <thead>
              <tr>
                <th class="col-email">
                  <div class="ant-form-item-label">
                    <label class="ant-form-item-required">
                      Email
                    </label>
                  </div>
                </th>
                <th class="col-password">
                  <div class="ant-form-item-label">
                    <label>
                      Password
                    </label>
                  </div>
                </th>
                <th class="col-expiry">
                  <div class="ant-form-item-label">
                    <label class="ant-form-item-required">
                      Expiry date
                    </label>
                  </div>
                </th>
                <th v-for="plan in PLANS" :key="plan.field" class="col-limit">
                  <div class="ant-form-item-label">
                    <label class="ant-form-item-required">
                      {{ plan.label }}
                    </label>
                  </div>
                </th>
                <th class="col-action" />
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in rows" :key="row.id">
                <td>
                  <a-form-item :validate-status="errorStatus(row, 'email')" :help="row.errors.email">
                    <a-input
                      v-model="row.email"
                      placeholder="Email"
                      autocomplete="off"
                      :disabled="isSaving"
                      @input="clearError(row, 'email')"
                    />
                  </a-form-item>
                </td>
                <td>
                  <a-form-item :validate-status="errorStatus(row, 'password')" :help="row.errors.password">
                    <a-input-password
                      v-model="row.password"
                      placeholder="Leave blank to auto-generate"
                      autocomplete="off"
                      :disabled="isSaving"
                      @input="clearError(row, 'password')"
                    />
                  </a-form-item>
                </td>
                <td>
                  <a-form-item :validate-status="errorStatus(row, 'expiryDate')" :help="row.errors.expiryDate">
                    <a-date-picker
                      v-model="row.expiryDate"
                      class="full-w"
                      placeholder="Choose Date"
                      :disabled="isSaving"
                      :disabled-date="disabledDate"
                      :disabled-time="disabledTime"
                      :show-time="{ format: 'HH:mm' }"
                      :format="DATE_FORMAT.DDMMMYYYYHHmm"
                      @change="clearError(row, 'expiryDate')"
                    />
                  </a-form-item>
                </td>
                <td v-for="plan in PLANS" :key="plan.field">
                  <a-form-item :validate-status="errorStatus(row, plan.field)" :help="row.errors[plan.field]">
                    <a-input
                      v-model="row[plan.field]"
                      :placeholder="plan.label"
                      :disabled="isSaving"
                      @input="clearError(row, plan.field)"
                    />
                  </a-form-item>
                </td>
                <td class="col-action">
                  <a-button
                    v-if="rows.length > 1"
                    type="danger"
                    size="large"
                    ghost
                    :disabled="isSaving"
                    @click="removeRow(index)"
                  >
                    <a-icon type="delete" />
                  </a-button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <a-button type="dashed" block class="mt-2" :disabled="isSaving" @click="addRow">
          <a-icon type="plus" /> Add row
        </a-button>
      </template>

      <template v-else>
        <a-textarea
          v-model="bulkText"
          :placeholder="'Paste one email per line:\nuser@example.com\nanother@example.com'"
          :auto-size="{ minRows: 6, maxRows: 14 }"
        />
        <div class="bulk-status mt-2">
          <span class="bulk-preview-count">
            {{ bulkParsed.valid.length }} valid email{{ bulkParsed.valid.length !== 1 ? 's' : '' }}
          </span>
          <span v-if="bulkParsed.duplicates > 0" class="bulk-warning ml-2">
            ({{ bulkParsed.duplicates }} duplicate{{ bulkParsed.duplicates !== 1 ? 's' : '' }} removed)
          </span>
          <span v-if="bulkParsed.invalid.length > 0" class="bulk-error-count ml-2">
            {{ bulkParsed.invalid.length }} invalid
          </span>
        </div>
      </template>
    </div>
    <div class="a-footer">
      <a-button key="back" size="large" class="mr-3 slide-down-enter-active" :disabled="isSaving">
        <router-link :to="'/user-management'">Cancel</router-link>
      </a-button>
      <a-button
        key="submit"
        size="large"
        type="primary"
        html-type="submit"
        class="slide-down-enter-active"
        :loading="isSaving"
      >
        Save
      </a-button>
    </div>
  </a-form>
</template>

<script>
  import moment from 'moment';
  import { range } from 'lodash';
  import { DATE_FORMAT } from '@constants/dateFormat';
  import UserManagementService from '../services/UserManagement.service';
  import { generatePassword } from '../helpers/generatePassword';
  import { parseEmailList, getEmailError } from '../helpers/parseEmailList';
  import { resolveSourceGrantIds } from '../helpers/copyRbac';
  import { STOP_STATUSES } from '../helpers/runSequentially';
  import { EXPIRY_DATE_FORMAT, PLANS } from '../consts/UserManagement.const';

  const MIN_PASSWORD_LENGTH = 8;
  const MAX_PASSWORD_LENGTH = 255;
  const WHOLE_NUMBER_PATTERN = /^\d+$/;
  // Unauthenticated, forbidden or rate limited: the remaining rows would fail the same way.
  const CREATE_STOP_STATUSES = [401, 403, 429];

  let rowIdCounter = 0;

  const createRow = (defaults, email = '') => ({
    id: rowIdCounter++,
    email,
    password: '',
    expiryDate: defaults.expiryDate && defaults.expiryDate.clone(),
    gold: defaults.gold,
    silver: defaults.silver,
    bronze: defaults.bronze,
    trial: defaults.trial,
    errors: {}
  });

  const isOutcomeUnknown = (error) => !error.status || error.status >= 500;

  const getErrorMessage = (error) => {
    if (error.status === 422) {
      // The shared interceptor keeps only the first field's messages, which is why the raw response body is read.
      const fieldMessages = Object.values(error.response?.data?.errors || {}).flat();
      if (fieldMessages.length) {
        return fieldMessages.join(' ');
      }
    }
    if (isOutcomeUnknown(error)) {
      return 'Outcome unknown. Check the user list before retrying.';
    }
    return (error.messages || []).join(' ') || 'Request failed.';
  };

  const escapeCsvField = (value) => (/[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

  export default {
    name: 'CreateUserAccount',
    beforeRouteLeave(to, from, next) {
      // A 401 redirects to /login, which must not be blocked.
      if (to.path === '/login') {
        return next();
      }
      if (this.isSaving) {
        return next(false);
      }
      if (!this.hasUndownloadedPasswords) {
        return next();
      }
      this.$confirm({
        title: 'Leave without downloading the CSV?',
        content: 'The passwords of the created users are only in the CSV and cannot be shown again.',
        okText: 'Leave',
        okType: 'danger',
        cancelText: 'Stay',
        onOk: () => next(),
        onCancel: () => next(false)
      });
    },
    data() {
      const defaults = { expiryDate: moment().add(1, 'year'), gold: '0', silver: '0', bronze: '0', trial: '0' };
      return {
        DATE_FORMAT,
        PLANS,
        defaults,
        rows: [createRow(defaults)],
        bulkMode: false,
        bulkText: '',
        createdUsers: [],
        csvDownloaded: false,
        failures: [],
        isSaving: false,
        rbacSourceId: undefined,
        rbacCopied: { sourceId: undefined, from: '', count: 0 },
        rbacForbiddenReason: '',
        users: [],
        isLoadingUsers: false
      };
    },
    computed: {
      hasUndownloadedPasswords() {
        return this.createdUsers.length > 0 && !this.csvDownloaded;
      },
      bulkParsed() {
        return parseEmailList(this.bulkText);
      },
      rbacSourceUser() {
        return this.users.find((user) => user.customerId === this.rbacSourceId);
      },
      rbacSourceGrantIds() {
        if (!this.rbacSourceUser) {
          return [];
        }
        return resolveSourceGrantIds(this.rbacSourceUser, this.users);
      },
      rbacHint() {
        if (!this.rbacSourceGrantIds.length) {
          return 'This user has no grants that can be copied.';
        }
        return `${this.rbacSourceGrantIds.length} grant(s) will be added to each new user.`;
      }
    },
    created() {
      this.loadUsers();
    },
    mounted() {
      window.addEventListener('beforeunload', this.warnBeforeUnload);
    },
    beforeDestroy() {
      window.removeEventListener('beforeunload', this.warnBeforeUnload);
    },
    methods: {
      // The picker is optional: without the list, users can still be created.
      async loadUsers() {
        this.isLoadingUsers = true;
        try {
          this.users = await UserManagementService.fetchListUsers();
        } catch (error) {
          this.$showMultiErrorMessage(error.messages);
        } finally {
          this.isLoadingUsers = false;
        }
      },
      newRow(email = '') {
        return createRow(this.defaults, email);
      },
      addRow() {
        this.rows.push(this.newRow());
      },
      removeRow(index) {
        this.rows.splice(index, 1);
      },
      toggleBulkMode() {
        if (this.bulkMode) {
          if (!this.checkBulkText()) {
            return;
          }
          // Rows whose email is still listed keep their other fields; emails no longer listed lose their row.
          const rowsByEmail = new Map();
          this.rows.forEach((row) => {
            const key = row.email.trim().toLowerCase();
            if (!rowsByEmail.has(key)) {
              rowsByEmail.set(key, row);
            }
          });
          const rows = this.bulkParsed.valid.map((email) => {
            const row = rowsByEmail.get(email.toLowerCase());
            if (!row) {
              return this.newRow(email);
            }
            row.email = email;
            return row;
          });
          this.rows = rows.length ? rows : [this.newRow()];
        } else {
          this.bulkText = this.rows
            .map((row) => row.email.trim())
            .filter(Boolean)
            .join('\n');
        }
        this.bulkMode = !this.bulkMode;
      },
      // Returns false, after showing why, when the bulk text holds invalid emails.
      checkBulkText() {
        const { invalid } = this.bulkParsed;
        if (invalid.length) {
          this.$showErrorMessage(`${invalid.length} invalid email(s) found. Please fix them before saving.`);
          return false;
        }
        return true;
      },
      errorStatus(row, field) {
        return row.errors[field] ? 'error' : '';
      },
      clearError(row, field) {
        if (row.errors[field]) {
          row.errors = { ...row.errors, [field]: '' };
        }
      },
      validateRow(row, seenEmails) {
        const errors = {};
        const email = row.email.trim();
        const emailError = email ? getEmailError(email) : 'Please input an email.';
        if (emailError) {
          errors.email = emailError;
        } else if (seenEmails.has(email.toLowerCase())) {
          errors.email = 'This email is already used on this page.';
        } else {
          seenEmails.add(email.toLowerCase());
        }
        if (row.password && (row.password.length < MIN_PASSWORD_LENGTH || row.password.length > MAX_PASSWORD_LENGTH)) {
          errors.password = `Password must be ${MIN_PASSWORD_LENGTH} to ${MAX_PASSWORD_LENGTH} characters.`;
        }
        if (!row.expiryDate) {
          errors.expiryDate = 'Please select an expiry date.';
        } else if (row.expiryDate.isBefore(moment().startOf('day'))) {
          errors.expiryDate = 'Expiry date cannot be in the past.';
        }
        PLANS.forEach(({ field }) => {
          const value = String(row[field]).trim();
          if (!value) {
            errors[field] = 'This field is required.';
          } else if (!WHOLE_NUMBER_PATTERN.test(value)) {
            errors[field] = 'Enter a whole number, 0 or more.';
          }
        });
        return errors;
      },
      validateRows() {
        const seenEmails = new Set(this.createdUsers.map((user) => user.email.toLowerCase()));
        let isValid = true;
        this.rows.forEach((row) => {
          row.errors = this.validateRow(row, seenEmails);
          isValid = isValid && !Object.keys(row.errors).length;
        });
        return isValid;
      },
      async createUser(row) {
        const password = row.password || generatePassword();
        const created = await UserManagementService.createUser({
          username: row.email.trim(),
          password,
          gold_limit: Number(row.gold),
          silver_limit: Number(row.silver),
          bronze_limit: Number(row.bronze),
          trial_limit: Number(row.trial),
          expiry_date: row.expiryDate.format(EXPIRY_DATE_FORMAT)
        });
        this.createdUsers.push({ email: row.email.trim(), password });
        this.csvDownloaded = false;
        return created;
      },
      // The user already exists, so a failed copy is reported without failing the row.
      // Returns false when the remaining users would fail the same way.
      async copyRbac(email, userId, grantIds) {
        if (!Number.isInteger(userId)) {
          this.failures.push(`${email}: created, RBAC not copied: server did not return the new user id`);
          return true;
        }
        try {
          await UserManagementService.batchAddRbacPermissions(userId, grantIds);
          this.rbacCopied.count += 1;
        } catch (error) {
          this.failures.push(
            isOutcomeUnknown(error)
              ? `${email}: created, RBAC copy outcome unknown. Check the user's RBAC tab.`
              : `${email}: created, RBAC not copied: ${getErrorMessage(error)}`
          );
          // A 403 concerns the grants, not the session: later users can still be created, but would be refused too.
          if (error.status === 403) {
            this.rbacForbiddenReason = getErrorMessage(error);
          }
          return !STOP_STATUSES.includes(error.status);
        }
        return true;
      },
      // The grants may have changed since the picker was loaded, so they are read again before anyone is created.
      // Returns null, after showing why, when they cannot be read or are empty.
      async fetchSourceGrantIds() {
        try {
          const users = await UserManagementService.fetchListUsers();
          const source = users.find((user) => user.customerId === this.rbacSourceId);
          if (!source) {
            this.$showErrorMessage('The user to copy RBAC from no longer exists.');
            return null;
          }
          const grantIds = resolveSourceGrantIds(source, users);
          if (grantIds.length) {
            return grantIds;
          }
          this.$showWarningMessage(`${source.userName} no longer has grants that can be copied.`);
        } catch (error) {
          this.$showMultiErrorMessage(error.messages);
        }
        return null;
      },
      // One request at a time, in row order. Created rows leave the table; failed rows stay for a retry.
      async saveRows(grantIds) {
        let hasSkippedRbac = false;
        for (const row of [...this.rows]) {
          try {
            const created = await this.createUser(row);
            this.rows.splice(this.rows.indexOf(row), 1);
            if (this.rbacForbiddenReason) {
              hasSkippedRbac = true;
            } else if (grantIds.length) {
              const canContinue = await this.copyRbac(row.email.trim(), created?.id, grantIds);
              if (!canContinue) {
                break;
              }
            }
          } catch (error) {
            this.failures.push(`${row.email.trim()}: ${getErrorMessage(error)}`);
            if (CREATE_STOP_STATUSES.includes(error.status)) {
              break;
            }
          }
        }
        if (hasSkippedRbac) {
          this.failures.push(`RBAC not copied for the remaining users: ${this.rbacForbiddenReason}`);
        }
        if (!this.rows.length) {
          this.rows.push(this.newRow());
        }
      },
      async onSubmit(e) {
        e.preventDefault();
        if (this.isSaving) {
          return;
        }
        if (this.bulkMode) {
          if (!this.checkBulkText()) {
            return;
          }
          if (!this.bulkParsed.valid.length) {
            this.$showErrorMessage('No valid emails entered.');
            return;
          }
          this.toggleBulkMode();
        }
        if (!this.validateRows()) {
          this.$showErrorMessage('Please fix the highlighted fields before saving.');
          return;
        }
        this.isSaving = true;
        try {
          const grantIds = this.rbacSourceUser ? await this.fetchSourceGrantIds() : [];
          if (!grantIds) {
            return;
          }
          this.failures = [];
          // The copied count accumulates across saves, like the created count, until the source changes.
          if (this.rbacCopied.sourceId !== this.rbacSourceId) {
            this.rbacCopied = { sourceId: this.rbacSourceId, from: this.rbacSourceUser ? this.rbacSourceUser.userName : '', count: 0 };
          }
          this.rbacForbiddenReason = '';
          await this.saveRows(grantIds);
        } finally {
          this.isSaving = false;
        }
      },
      warnBeforeUnload(event) {
        if (this.isSaving || this.hasUndownloadedPasswords) {
          event.preventDefault();
          event.returnValue = '';
        }
      },
      downloadCsv() {
        const lines = [['email', 'password'], ...this.createdUsers.map(({ email, password }) => [email, password])];
        // The BOM makes Excel read the file as UTF-8.
        const csv = `\uFEFF${lines.map((line) => line.map(escapeCsvField).join(',')).join('\r\n')}`;
        const url = window.URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = `new-weborion-user-${moment().format('DD-MM-YYYY')}.csv`;
        document.body.appendChild(link);
        link.click();
        this.csvDownloaded = true;
        window.setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 1000);
      },
      disabledDate(current) {
        return current && current.isBefore(moment().startOf('day'));
      },
      disabledTime(selected) {
        const now = moment();
        const isToday = !selected || selected.isSame(now, 'day');
        const isCurrentHour = !selected || selected.isSame(now, 'hour');
        return {
          disabledHours: () => (isToday ? range(0, now.hour()) : []),
          disabledMinutes: () => (isCurrentHour ? range(0, now.minute()) : [])
        };
      }
    }
  };
</script>

<style lang="scss" scoped>
  .success-container {
    padding: 8px 10px;
    border: 1px solid #7ecf42;
    border-radius: 4px;
    background-color: #f3ffea;
    display: flex;
    align-items: flex-start;
    .success-icon {
      font-size: 40px;
      color: rgb(126, 207, 66);
    }
  }

  .error-container {
    padding: 8px 10px;
    border: 1px solid #ff4d4f;
    border-radius: 4px;
    background-color: #fff2f0;
    display: flex;
    align-items: flex-start;
    .error-icon {
      font-size: 40px;
      color: #ff4d4f;
    }
  }

  .notice-text {
    font-size: 1rem;
  }

  .defaults-bar {
    padding: 12px 16px;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
  }

  .defaults-title {
    font-weight: 500;
  }

  .defaults-fields {
    display: flex;
    flex-wrap: wrap;
  }

  .defaults-field {
    display: flex;
    flex-direction: column;
    margin-right: 8px;
  }

  .defaults-label {
    color: $prLgt;
    font-size: 13px;
  }

  .defaults-hint {
    color: $prLgt;
    font-size: 12px;
  }

  .users-label {
    font-weight: 500;
    margin-bottom: 0;
  }

  .users-count {
    color: $prLgt;
    font-size: 13px;
  }

  .user-table-wrapper {
    flex-shrink: 0;
    overflow-x: auto;
  }

  .user-table {
    width: 100%;
    table-layout: fixed;

    th,
    td {
      padding: 0 8px 12px 0;
      vertical-align: top;
    }

    .ant-form-item {
      margin-bottom: 0;
    }

    .col-action {
      width: 80px;
      padding-right: 0;
    }
  }

  .col-email {
    width: 280px;
  }

  .col-password {
    width: 220px;
  }

  .col-expiry {
    width: 200px;
  }

  .col-limit {
    width: 110px;
  }

  .col-rbac {
    width: 280px;
  }

  .bulk-status {
    font-size: 13px;
  }

  .bulk-preview-count {
    color: $prLgt;
  }

  .bulk-warning {
    color: #faad14;
  }

  .bulk-error-count {
    color: #f5222d;
  }
</style>
